import { useState } from 'react';
import { Link } from 'react-router-dom';
import { comics, rankings } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

type RankTab = 'weekly' | 'monthly' | 'total';

const tabLabels: Record<RankTab, string> = {
  weekly: '周榜',
  monthly: '月榜',
  total: '总榜',
};

function getComicById(id: number) {
  return comics.find(c => c.id === id);
}

export default function RankingSection() {
  const [activeTab, setActiveTab] = useState<RankTab>('weekly');
  const rankList = rankings[activeTab].map(id => getComicById(id)).filter(Boolean);

  return (
    <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full border border-foreground-200 text-foreground-500 text-xs font-medium mb-4">
              热门榜单
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950">
              韩漫排行榜
              <span className="text-foreground-400 font-normal"> TOP10</span>
            </h2>

            <div className="flex items-center gap-2 mt-6">
              {(Object.keys(tabLabels) as RankTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/80 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {tabLabels[tab]}
                </button>
              ))}
            </div>

            <p className="mt-6 text-sm text-foreground-500 leading-relaxed max-w-md">
              收录超过 {12_800} 部韩漫作品，涵盖连载与完结经典。榜单根据真实阅读数据每日更新，助你发现最受欢迎的韩漫作品。
            </p>

            <div className="flex items-center gap-8 mt-8">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-500">12,800+</div>
                <div className="text-xs text-foreground-400 mt-1">收录作品</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-500">50万+</div>
                <div className="text-xs text-foreground-400 mt-1">日活跃用户</div>
              </div>
            </div>

            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 mt-8 text-sm text-accent-600 hover:text-accent-700 font-medium transition-colors border-b border-accent-300 pb-0.5"
            >
              查看完整榜单
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="space-y-3">
            {rankList.slice(0, 5).map((comic, index) => {
              if (!comic) return null;
              return (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="flex items-center gap-4 p-3 rounded-xl bg-background-50 hover:bg-background-200/60 transition-colors group"
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold shrink-0 ${
                    index === 0
                      ? 'bg-amber-400 text-background-50'
                      : index === 1
                      ? 'bg-foreground-300 text-background-50'
                      : index === 2
                      ? 'bg-amber-600 text-background-50'
                      : 'bg-background-200 text-foreground-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-12 h-16 rounded-md overflow-hidden bg-background-300 shrink-0">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                      {comic.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-amber-500 flex items-center gap-0.5">
                        <i className="ri-star-fill text-[10px]"></i>
                        {comic.rating}
                      </span>
                      <span className="text-xs text-foreground-400">{comic.category}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}